import React, { useState } from 'react'
import { Box, Grid, Divider, useTheme, useMediaQuery } from '@mui/material'
import { useTranslation } from 'react-i18next'
import ListCard from './ListCard'
import ListService from 'api/services/Lists'
import CustomToggleButton from 'components/shared/CustomToggleButton'
import CustomToggleButtonGroup from 'components/shared/CustomToggleButtonGroup'
import ListInstructions from './ListInstructions'
import BasePagination from 'components/shared/BasePagination'
import TreePNG from 'assets/img/land-single-tree.png'
import SmallBuildingPNG from 'assets/img/small-building.png'
import BigBuildingPNG from 'assets/img/big-building.png'
import KatoikiaMobilePNG from 'assets/img/katoikia-mobile-121x126.png'
import { ListCategoryEnum } from 'types/enums'
import usePageView from 'hooks/usePageView'

enum PROPERTY_TYPES {
  Commercial = 'Commercial',
  Estate = 'Estate',
  Land = 'Land',
}

enum LISTING_TYPES {
  Sale = 'Sale',
  Rent = 'Rent',
}

const getListsSearchCriteria = (propertyType, listingType) => {
  if (propertyType === undefined && listingType === undefined) return []

  if (!listingType) {
    if (propertyType === PROPERTY_TYPES.Estate)
      return [
        ListCategoryEnum.EstatePropertySales,
        ListCategoryEnum.EstatePropertyRent,
      ]
    if (propertyType === PROPERTY_TYPES.Commercial)
      return [
        ListCategoryEnum.EstateCommercialSales,
        ListCategoryEnum.EstateCommercialRent,
      ]
    if (propertyType === PROPERTY_TYPES.Land)
      return [ListCategoryEnum.EstateLandSales, ListCategoryEnum.EstateLandRent]
  }

  if (listingType === LISTING_TYPES.Sale) {
    if (propertyType === PROPERTY_TYPES.Estate)
      return [ListCategoryEnum.EstatePropertySales]
    if (propertyType === PROPERTY_TYPES.Commercial)
      return [ListCategoryEnum.EstateCommercialSales]
    if (propertyType === PROPERTY_TYPES.Land)
      return [ListCategoryEnum.EstateLandSales]
    if (!propertyType)
      return [
        ListCategoryEnum.EstatePropertySales,
        ListCategoryEnum.EstateCommercialSales,
        ListCategoryEnum.EstateLandSales,
      ]
  }

  if (listingType === LISTING_TYPES.Rent) {
    if (propertyType === PROPERTY_TYPES.Estate)
      return [ListCategoryEnum.EstatePropertyRent]
    if (propertyType === PROPERTY_TYPES.Commercial)
      return [ListCategoryEnum.EstateCommercialRent]
    if (propertyType === PROPERTY_TYPES.Land)
      return [ListCategoryEnum.EstateLandRent]
    if (!propertyType)
      return [
        ListCategoryEnum.EstatePropertyRent,
        ListCategoryEnum.EstateCommercialRent,
        ListCategoryEnum.EstateLandRent,
      ]
  }

  return []
}

function FavoritesLists() {
  const { sendPageView } = usePageView({ runOnMount: false })
  const { t } = useTranslation()

  const theme = useTheme()

  const [page, setPage] = useState<number>(0)
  const handleChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value - 1)
  }

  const PER_PAGE = 12

  const [propertyType, setPropertyType] = useState<string | null>(null)
  const [listingType, setListingType] = useState<string | null>(null)

  const SEARCH_CRITERIA = React.useMemo(() => {
    return getListsSearchCriteria(propertyType, listingType)
  }, [propertyType, listingType])

  const searchMutation = ListService.hooks.useSearchByCriteriaMutation({
    onSettled: () => {
      sendPageView()
    },
  })
  const countMutation = ListService.hooks.useCountByCriteriaMutation({
    onSuccess: (data) => {
      if (data) {
        searchMutation.mutate({
          listCategories: SEARCH_CRITERIA,
          pageNumber: page,
          pageSize: PER_PAGE,
        })
      }
    },
  })

  React.useEffect(() => {
    searchMutation.reset()
    countMutation.mutate({
      listCategories: SEARCH_CRITERIA,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyType, listingType])

  React.useEffect(() => {
    searchMutation.reset()
    countMutation.mutate({
      listCategories: SEARCH_CRITERIA,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page])

  const handlePropertyType = (
    event: React.MouseEvent<HTMLElement>,
    newValue: string | null
  ) => {
    setPage(0)
    setPropertyType(newValue)
  }

  const handleListingType = (
    event: React.MouseEvent<HTMLElement>,
    newValue: string | null
  ) => {
    setPage(0)
    setListingType(newValue)
  }
  const listInstructions = [
    { number: 1, text: t('listInstructions.searchAndFind'), icon: TreePNG },
    { number: 2, text: t('listInstructions.clickOn'), icon: SmallBuildingPNG },
    {
      number: 3,
      text: t('listInstructions.createNewList'),
      icon: BigBuildingPNG,
    },
    {
      number: 4,
      text: t('listInstructions.listIsReady'),
      icon: KatoikiaMobilePNG,
    },
  ]

  const matchesSM = useMediaQuery(theme.breakpoints.down('md'))

  return (
    <Box sx={{ mt: '10px' }}>
      {getListsSearchCriteria(propertyType, listingType)?.length === 0 &&
      !countMutation.isLoading &&
      countMutation?.data === 0 ? (
        <ListInstructions instructions={listInstructions} titleList={true} />
      ) : null}
      {(countMutation.isSuccess && countMutation?.data) ||
      getListsSearchCriteria(propertyType, listingType)?.length ? (
        <>
          <Grid
            container
            direction="row"
            justifyContent={'center'}
            sx={{ flexWrap: 'wrap' }}
          >
            <Grid
              item
              xs
              sx={{
                pl: { xs: '50px', lg: 0 },
                maxWidth: { xs: '360px', sm: '100%' },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  rowGap: '16px',
                  flexWrap: 'wrap',
                  justifyContent: { xs: 'flex-end', lg: 'flex-start' },
                  columnGap: { xs: '12px', lg: '20px' },
                  pr: { xs: '0px', sm: '40px', lg: '8px' },
                }}
              >
                <CustomToggleButtonGroup
                  value={propertyType}
                  exclusive
                  onChange={handlePropertyType}
                >
                  <CustomToggleButton
                    value={PROPERTY_TYPES.Estate}
                    sx={{ boxShadow: 4, color: 'common.black' }}
                  >
                    {t('listsFilter.estate')}
                  </CustomToggleButton>
                  <CustomToggleButton
                    value={PROPERTY_TYPES.Commercial}
                    sx={{ boxShadow: 4, color: 'common.black' }}
                  >
                    {t('listsFilter.commercial')}
                  </CustomToggleButton>
                  <CustomToggleButton
                    value={PROPERTY_TYPES.Land}
                    sx={{ boxShadow: 4, color: 'common.black' }}
                  >
                    {t('listsFilter.land')}{' '}
                  </CustomToggleButton>
                </CustomToggleButtonGroup>
                {matchesSM ? null : (
                  <Divider
                    orientation="vertical"
                    variant="fullWidth"
                    flexItem
                    sx={{
                      bgcolor: 'black',
                    }}
                  />
                )}
                <CustomToggleButtonGroup
                  value={listingType}
                  exclusive
                  onChange={handleListingType}
                >
                  <CustomToggleButton
                    value={LISTING_TYPES.Rent}
                    sx={{ boxShadow: 4, color: 'common.black' }}
                  >
                    {' '}
                    {t('listsFilter.rent')}
                  </CustomToggleButton>
                  <CustomToggleButton
                    value={LISTING_TYPES.Sale}
                    sx={{ boxShadow: 4, color: 'common.black' }}
                  >
                    {' '}
                    {t('listsFilter.sale')}
                  </CustomToggleButton>
                </CustomToggleButtonGroup>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box
                sx={{
                  mt: { xs: '13px', sm: '56px', lg: '33px' },
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: { xs: 'center', lg: 'flex-start' },
                  alignItems: 'center',
                  gap: { xs: '14px', sm: '24px', lg: '50px' },
                  [theme.breakpoints.down('sm')]: {
                    display: 'grid',
                    gridTemplateColumns: `repeat(2,  176px)`,
                    alignContent: 'center',
                    justifyContent: 'center',
                    alignItems: { xs: 'center', md: 'flex-start' },
                  },
                }}
              >
                {searchMutation.isSuccess
                  ? searchMutation?.data?.userFavoritesList?.map(
                      (item, index) => <ListCard item={item} key={index} />
                    )
                  : ''}
              </Box>
            </Grid>
          </Grid>
          {countMutation?.data && countMutation.data >= PER_PAGE ? (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                mt: { xs: '30px', sm: '138px' },
              }}
            >
              <BasePagination
                count={Math.ceil((countMutation.data ?? 0) / PER_PAGE)}
                page={page + 1}
                onChange={handleChange}
              />
            </Box>
          ) : null}
        </>
      ) : null}
    </Box>
  )
}
export default FavoritesLists
